import React, { useState, useEffect, useRef } from 'react';
import { firestore } from '../firebase';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/solid';

function AddBranch({ onClose, currentBranch, featureOptions = [], seatingOptions = [] }) {
  const RAZORPAY_ACCOUNT_OPTIONS = [
    { value: '', label: 'Not selected' },
    { value: 'thegot', label: 'TheGot Hospitality' },
    { value: 'muhavra', label: 'Muhavra Enterprises' },
  ];

  const [branchName, setBranchName] = useState('');
  const [branchCode, setBranchCode] = useState('');
  const [storeType, setStoreType] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [directionsLink, setDirectionsLink] = useState('');
  const [orderingEnabled, setOrderingEnabled] = useState(false);
  const [razorpayAccount, setRazorpayAccount] = useState('');
  const [features, setFeatures] = useState([]);
  const [seating, setSeating] = useState([]);
  const [featureDropdownOpen, setFeatureDropdownOpen] = useState(false);
  const [seatingDropdownOpen, setSeatingDropdownOpen] = useState(false);
  const [featureSearchQuery, setFeatureSearchQuery] = useState('');
  const [seatingSearchQuery, setSeatingSearchQuery] = useState('');
  const featureDropdownRef = useRef(null);
  const seatingDropdownRef = useRef(null);

  // If we're editing, pre-fill the form with the current branch's data
  useEffect(() => {
    if (currentBranch) {
      setBranchName(currentBranch.branchName || '');
      setBranchCode(currentBranch.branchCode || '');
      setStoreType(currentBranch.storeType || '');
      setAddress(currentBranch.address || '');
      setCity(currentBranch.city || '');
      setPhone(currentBranch.phone || '');
      setStartTime(currentBranch.startTime || '');
      setEndTime(currentBranch.endTime || '');
      setLatitude(currentBranch.latitude || '');
      setLongitude(currentBranch.longitude || '');
      setDirectionsLink(currentBranch.directionsLink || '');
      setOrderingEnabled(currentBranch.orderingEnabled === true);
      setRazorpayAccount(currentBranch.razorpayAccount || '');
      setFeatures(Array.isArray(currentBranch.features) ? currentBranch.features : []);
      setSeating(Array.isArray(currentBranch.seating) ? currentBranch.seating : []);
    } else {
      setBranchName('');
      setBranchCode('');
      setStoreType('');
      setAddress('');
      setCity('');
      setPhone('');
      setStartTime('');
      setEndTime('');
      setLatitude('');
      setLongitude('');
      setDirectionsLink('');
      setOrderingEnabled(false);
      setRazorpayAccount('');
      setFeatures([]);
      setSeating([]);
    }
  }, [currentBranch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (featureDropdownRef.current && !featureDropdownRef.current.contains(event.target)) {
        setFeatureDropdownOpen(false);
        setFeatureSearchQuery('');
      }

      if (seatingDropdownRef.current && !seatingDropdownRef.current.contains(event.target)) {
        setSeatingDropdownOpen(false);
        setSeatingSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Helper function to generate image URL from branch name
  const getBranchImageUrl = (name) => {
    if (!name) return '';
    return `https://cdn.shopify.com/s/files/1/0549/6268/4053/t/3/assets/${name}.webp`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!branchName || !branchCode) {
      alert('Please provide Branch Name and Branch Code.');
      return;
    }

    const newBranch = {
      branchName,
      branchCode,
      storeType: storeType || '',
      address: address || '',
      city: city || '',
      phone: phone || '',
      startTime: startTime || '',
      endTime: endTime || '',
      latitude: latitude || '',
      longitude: longitude || '',
      directionsLink: directionsLink || '',
      orderingEnabled,
      razorpayAccount: orderingEnabled ? razorpayAccount : '',
      features,
      seating,
    };

    if (currentBranch) {
      // Update existing branch
      await firestore.collection('branches').doc(currentBranch.id).update(newBranch);
    } else {
      // Add new branch
      await firestore.collection('branches').add(newBranch);
    }

    onClose();
  };

  const normalizeOption = (value) => value.trim();

  const getCombinedOptions = (baseOptions, selectedValues, searchQuery) => {
    const uniqueValues = new Set();

    [...baseOptions, ...selectedValues]
      .filter((value) => typeof value === 'string' && value.trim())
      .forEach((value) => uniqueValues.add(normalizeOption(value)));

    return Array.from(uniqueValues)
      .filter((value) => value.toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((a, b) => a.localeCompare(b));
  };

  const toggleMultiSelectValue = (value, selectedValues, setSelectedValues) => {
    if (selectedValues.includes(value)) {
      setSelectedValues(selectedValues.filter((item) => item !== value));
      return;
    }

    setSelectedValues([...selectedValues, value]);
  };

  const addCustomValue = (rawValue, selectedValues, setSelectedValues, clearSearch) => {
    const normalizedValue = normalizeOption(rawValue);

    if (!normalizedValue) {
      return;
    }

    if (!selectedValues.includes(normalizedValue)) {
      setSelectedValues([...selectedValues, normalizedValue]);
    }

    clearSearch('');
  };

  const renderMultiSelect = ({
    label,
    selectedValues,
    setSelectedValues,
    searchQuery,
    setSearchQuery,
    dropdownOpen,
    setDropdownOpen,
    dropdownRef,
    baseOptions,
    emptyLabel,
    searchPlaceholder,
    createLabel,
  }) => {
    const availableOptions = getCombinedOptions(baseOptions, selectedValues, searchQuery);
    const normalizedSearch = normalizeOption(searchQuery);
    const canCreate = normalizedSearch && !availableOptions.some(
      (option) => option.toLowerCase() === normalizedSearch.toLowerCase()
    );

    return (
      <div className="mb-4" ref={dropdownRef}>
        <label className="block mb-1 font-medium">{label}</label>
        <div className="relative">
          <div
            className="w-full border px-3 py-2 rounded cursor-pointer bg-white min-h-[42px] flex items-center justify-between"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <div className="flex flex-wrap gap-1 flex-1">
              {selectedValues.length === 0 ? (
                <span className="text-gray-400">{emptyLabel}</span>
              ) : (
                selectedValues.map((value) => (
                  <span
                    key={value}
                    className="bg-teal-500 text-white px-2 py-1 rounded text-sm flex items-center gap-1"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedValues(selectedValues.filter((item) => item !== value));
                    }}
                  >
                    {value}
                    <XMarkIcon className="h-3 w-3 cursor-pointer hover:text-gray-200" />
                  </span>
                ))
              )}
            </div>
            <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
          </div>

          {dropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-72 overflow-hidden">
              <div className="p-2 border-b">
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded outline-none focus:border-teal-500"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && canCreate) {
                      event.preventDefault();
                      addCustomValue(searchQuery, selectedValues, setSelectedValues, setSearchQuery);
                    }
                  }}
                />
              </div>

              <div className="max-h-56 overflow-y-auto">
                {canCreate && (
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-teal-600 hover:bg-teal-50 border-b"
                    onClick={() => addCustomValue(searchQuery, selectedValues, setSelectedValues, setSearchQuery)}
                  >
                    {createLabel} "{normalizedSearch}"
                  </button>
                )}

                {availableOptions.length === 0 ? (
                  <div className="px-3 py-2 text-gray-400 text-sm">No options found</div>
                ) : (
                  availableOptions.map((value) => (
                    <div
                      key={value}
                      className={`px-3 py-2 cursor-pointer hover:bg-gray-100 flex items-center gap-2 ${
                        selectedValues.includes(value) ? 'bg-teal-50' : ''
                      }`}
                      onClick={() => toggleMultiSelectValue(value, selectedValues, setSelectedValues)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedValues.includes(value)}
                        onChange={() => {}}
                        className="h-4 w-4"
                      />
                      <span>{value}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4">{currentBranch ? 'Edit Branch' : 'Add Branch'}</h3>

        {/* Branch Image Preview */}
        {branchName && (
          <div className="mb-6 flex justify-center">
            <img
              src={getBranchImageUrl(currentBranch?.storeName || branchName)}
              alt={branchName}
              className="w-48 h-48 object-cover rounded-lg shadow"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" dy="105" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
              }}
            />
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block mb-1 font-medium">Branch Name *</label>
              <input
                type="text"
                className="w-full border px-3 py-2 rounded"
                value={branchName}
                onChange={(e) => setBranchName(e.target.value)}
                required
              />
            </div>
            <div className="mb-4">
              <label className="block mb-1 font-medium">Branch Code *</label>
              <input
                type="text"
                className="w-full border px-3 py-2 rounded"
                value={branchCode}
                onChange={(e) => setBranchCode(e.target.value)}
                required
              />
            </div>
            <div className="mb-4">
              <label className="block mb-1 font-medium">Store Type</label>
              <select
                className="w-full border px-3 py-2 rounded bg-white"
                value={storeType}
                onChange={(e) => setStoreType(e.target.value)}
              >
                <option value="">Select Store Type</option>
                <option value="Mall Outlet">Mall Outlet</option>
                <option value="Standalone Store">Standalone Store</option>
                <option value="Kiosk">Kiosk</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block mb-1 font-medium">Address</label>
            <textarea
              className="w-full border px-3 py-2 rounded"
              rows="2"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block mb-1 font-medium">City</label>
              <input
                type="text"
                className="w-full border px-3 py-2 rounded"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label className="block mb-1 font-medium">Phone</label>
              <input
                type="tel"
                className="w-full border px-3 py-2 rounded"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block mb-1 font-medium">Start Time</label>
              <input
                type="time"
                className="w-full border px-3 py-2 rounded"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label className="block mb-1 font-medium">End Time</label>
              <input
                type="time"
                className="w-full border px-3 py-2 rounded"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block mb-1 font-medium">Latitude</label>
              <input
                type="text"
                className="w-full border px-3 py-2 rounded"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="e.g., 28.6670030679"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-1 font-medium">Longitude</label>
              <input
                type="text"
                className="w-full border px-3 py-2 rounded"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="e.g., 77.1221663134"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block mb-1 font-medium">Directions Link</label>
            <input
              type="url"
              className="w-full border px-3 py-2 rounded"
              value={directionsLink}
              onChange={(e) => setDirectionsLink(e.target.value)}
              placeholder="https://maps.app.goo.gl/..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block mb-1 font-medium">Online Ordering</label>
              <label className="flex items-center gap-3 border px-3 py-2 rounded min-h-[42px] cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={orderingEnabled}
                  onChange={(e) => setOrderingEnabled(e.target.checked)}
                />
                <span>{orderingEnabled ? 'Enabled' : 'Disabled'}</span>
              </label>
            </div>
            <div className="mb-4">
              <label className="block mb-1 font-medium">Razorpay Account</label>
              <select
                className="w-full border px-3 py-2 rounded bg-white disabled:bg-gray-100 disabled:text-gray-500"
                value={razorpayAccount}
                onChange={(e) => setRazorpayAccount(e.target.value)}
                disabled={!orderingEnabled}
              >
                {RAZORPAY_ACCOUNT_OPTIONS.map((option) => (
                  <option key={option.value || 'empty'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderMultiSelect({
              label: 'Seating',
              selectedValues: seating,
              setSelectedValues: setSeating,
              searchQuery: seatingSearchQuery,
              setSearchQuery: setSeatingSearchQuery,
              dropdownOpen: seatingDropdownOpen,
              setDropdownOpen: setSeatingDropdownOpen,
              dropdownRef: seatingDropdownRef,
              baseOptions: seatingOptions,
              emptyLabel: 'Select or create seating options',
              searchPlaceholder: 'Search or add seating...',
              createLabel: 'Add seating',
            })}

            {renderMultiSelect({
              label: 'Features',
              selectedValues: features,
              setSelectedValues: setFeatures,
              searchQuery: featureSearchQuery,
              setSearchQuery: setFeatureSearchQuery,
              dropdownOpen: featureDropdownOpen,
              setDropdownOpen: setFeatureDropdownOpen,
              dropdownRef: featureDropdownRef,
              baseOptions: featureOptions,
              emptyLabel: 'Select or create feature options',
              searchPlaceholder: 'Search or add features...',
              createLabel: 'Add feature',
            })}
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded border"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-teal-500 text-white px-4 py-2 rounded"
            >
              {currentBranch ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddBranch;
